/**
 * SidebarLogo
 *
 * Renders the brand mark at the top of the sidebar.
 *
 *   - **Expanded**: tenant wordmark from Redux (workspace logo for
 *     workspace admins via useWorkspaceBranding; the account-level
 *     Dentlogics wordmark otherwise). Falls back to the bundled wordmark asset.
 *   - **Collapsed**: the tenant's **favicon** when one is explicitly
 *     uploaded (Redux `state.account.favicon`). Falls back to the
 *     bundled PWA icon (square Dentlogics mark). Workspaces typically
 *     upload wide wordmarks as their logo, so we don't auto-fallback
 *     here — only the actual favicon swaps in. Admins who want a
 *     branded collapsed rail upload a square favicon on the workspace
 *     branding tab.
 */

import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Logo from '../../assets/images/logo.webp';
import { Box, useSidebarExpanded } from '../ui';
import { RootState } from '../../types';

const ICON_LOGO_PATH = '/logo192.png';

export default function SidebarLogo() {
  const navigate = useNavigate();
  const expanded = useSidebarExpanded();
  const accountName = useSelector((state: RootState) => state.account.name);
  const accountLogo = useSelector((state: RootState) => state.account.logoUrl);
  const accountFavicon = useSelector((state: RootState) => (state.account as any).favicon as string | null);

  // Expanded: tenant wordmark when available, bundled wordmark
  // otherwise. Collapsed: tenant favicon when explicitly uploaded,
  // bundled PWA icon otherwise (no logo fallback — wordmarks look
  // bad in a 40px slot).
  const src = expanded ? (accountLogo || Logo) : (accountFavicon || ICON_LOGO_PATH);

  return (
    <Box
      component="img"
      src={src}
      alt={accountName || 'LMS'}
      onClick={() => navigate('/courses')}
      sx={{
        // Fixed height in both states (objectFit: contain centers the image
        // within), so only width animates — `auto` heights aren't
        // animatable in CSS and produce a snap. Width animation matches the
        // sidebar's own easing/duration for a single coordinated motion.
        width: expanded ? 180 : 40,
        height: 56,
        objectFit: 'contain',
        cursor: 'pointer',
        py: 1.5,
        boxSizing: 'content-box',
        transition: 'width 280ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    />
  );
}
